import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SignupCompleteResponse, UserType } from '../../types/auth';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user_type = (location.state as any)?.user_type as UserType;

  // 기본 정보
  const [name, setName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [phone, setPhone] = useState('');

  // 계정 정보
  const [username, setUsername] = useState('');
  const [emailLocal, setEmailLocal] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // 주소 (선택)
  const [zipCode, setZipCode] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');

  // 기관 정보
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainSuggestion, setDomainSuggestion] = useState<string | null>(null);

  // 아이디 중복확인 상태
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // 보호자 SMS 인증 상태 (14세 미만)
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianCode, setGuardianCode] = useState('');
  const [guardianVerified, setGuardianVerified] = useState(false);
  const [guardianVerificationToken, setGuardianVerificationToken] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeExpiry, setCodeExpiry] = useState<Date | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [testCode, setTestCode] = useState<string | null>(null); // 테스트 모드용

  // 14세 미만 학생 정보
  const [school, setSchool] = useState('');
  const [grade, setGrade] = useState('');

  // 보호자 동의 모달 상태
  const [showGuardianConsentModal, setShowGuardianConsentModal] = useState(false);
  const [guardianConsentAgreed, setGuardianConsentAgreed] = useState(false);

  // user_type이 없으면 처음부터 다시 시작
  useEffect(() => {
    if (!user_type) {
      navigate('/signup');
    }
  }, [user_type, navigate]);

  // 전화번호 포맷팅
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneNumber(e.target.value));
  };

  // 보호자 전화번호 변경
  const handleGuardianPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuardianPhone(formatPhoneNumber(e.target.value));
    // 전화번호 변경 시 인증 상태 초기화
    if (guardianVerified) {
      setGuardianVerified(false);
      setGuardianVerificationToken('');
    }
    setCodeSent(false);
    setGuardianCode('');
  };

  // 인증번호 발송 버튼 클릭 - 먼저 동의 모달 띄우기
  const handleSendCodeClick = () => {
    // 학생 이름 확인
    if (name.trim().length < 2) {
      setError('학생 이름을 먼저 입력해주세요. (최소 2자)');
      return;
    }
    const phoneNumbers = guardianPhone.replace(/[^\d]/g, '');
    if (phoneNumbers.length < 10) {
      setError('올바른 보호자 연락처를 입력해주세요.');
      return;
    }
    setError('');
    setGuardianConsentAgreed(false);
    setShowGuardianConsentModal(true);
  };

  // 모달에서 동의 후 실제 인증코드 발송
  const sendGuardianCode = async () => {
    setShowGuardianConsentModal(false);

    const phoneNumbers = guardianPhone.replace(/[^\d]/g, '');
    if (phoneNumbers.length < 10) {
      setError('올바른 보호자 연락처를 입력해주세요.');
      return;
    }

    setSendingCode(true);
    setError('');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('send-guardian-code', {
        body: { phone: phoneNumbers, studentName: name.trim() },
      });

      if (fnError) {
        console.error('Send code error:', fnError);
        setError('인증코드 발송 중 오류가 발생했습니다.');
        return;
      }

      if (!data?.success) {
        setError(data?.error || '인증코드 발송에 실패했습니다.');
        return;
      }

      setCodeSent(true);
      const expiryDate = new Date(Date.now() + (data.expiresIn || 300) * 1000);
      setCodeExpiry(expiryDate);
      setRemainingTime(data.expiresIn || 300);

      // 테스트 모드인 경우 테스트 코드 저장
      if (data.testMode && data.testCode) {
        setTestCode(data.testCode);
      }

      setError('');
    } catch (err) {
      console.error('Send code error:', err);
      setError('인증코드 발송 중 오류가 발생했습니다.');
    } finally {
      setSendingCode(false);
    }
  };

  // 인증코드 검증
  const verifyGuardianCode = async () => {
    if (!guardianCode || guardianCode.length !== 6) {
      setError('6자리 인증코드를 입력해주세요.');
      return;
    }

    setVerifyingCode(true);
    setError('');

    try {
      const phoneNumbers = guardianPhone.replace(/[^\d]/g, '');
      const { data, error: fnError } = await supabase.functions.invoke('verify-guardian-code', {
        body: { phone: phoneNumbers, code: guardianCode },
      });

      if (fnError) {
        console.error('Verify code error:', fnError);
        setError('인증코드 확인 중 오류가 발생했습니다.');
        return;
      }

      if (!data?.success) {
        const errorMsg = data?.error || '인증코드가 일치하지 않습니다.';
        setError(errorMsg);
        alert(errorMsg);
        setGuardianCode(''); // 틀리면 입력값 초기화
        return;
      }

      setGuardianVerified(true);
      setGuardianVerificationToken(data.verificationToken);
      setTestCode(null); // 인증 성공 시 테스트 코드 숨김
      setError('');
    } catch (err) {
      console.error('Verify code error:', err);
      setError('인증코드 확인 중 오류가 발생했습니다.');
    } finally {
      setVerifyingCode(false);
    }
  };

  // 남은 시간 타이머
  useEffect(() => {
    if (!codeSent || !codeExpiry) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.max(0, Math.floor((codeExpiry.getTime() - now.getTime()) / 1000));
      setRemainingTime(diff);

      if (diff === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [codeSent, codeExpiry]);


  // 이메일 도메인 목록
  const emailDomains = [
    'gmail.com',
    'naver.com',
    'daum.net',
    'kakao.com',
    'hanmail.net',
    'nate.com',
    'hotmail.com',
    'outlook.com',
    'yahoo.com',
    'yahoo.co.kr',
  ];

  // 도메인 선택 핸들러
  const handleDomainSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmailDomain(e.target.value);
    setDomainSuggestion(null); // 선택 시 제안 제거
  };

  // Levenshtein distance 계산
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix: number[][] = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  // 도메인 입력 시 실시간 오타 감지
  useEffect(() => {
    if (!emailDomain || emailDomain.length < 3) {
      setDomainSuggestion(null);
      return;
    }

    // 도메인이 목록에 이미 있으면 제안 안 함
    if (emailDomains.includes(emailDomain.toLowerCase())) {
      setDomainSuggestion(null);
      return;
    }

    // 가장 유사한 도메인 찾기
    let bestMatch: string | null = null;
    let bestDistance = Infinity;
    const THRESHOLD = 2;

    for (const domain of emailDomains) {
      const distance = levenshteinDistance(emailDomain.toLowerCase(), domain);
      if (distance <= THRESHOLD && distance < bestDistance) {
        bestDistance = distance;
        bestMatch = domain;
      }
    }

    setDomainSuggestion(bestMatch);
  }, [emailDomain]);

  // 제안 적용
  const applyDomainSuggestion = () => {
    if (domainSuggestion) {
      setEmailDomain(domainSuggestion);
      setDomainSuggestion(null);
    }
  };

  // 아이디 중복확인 (Edge Function 사용)
  const checkUsernameAvailability = async () => {
    if (!username.trim()) {
      setError('아이디를 입력해주세요.');
      return;
    }

    // 아이디 형식 검사 (영문, 숫자, 언더스코어만 허용, 4-20자)
    const usernameRegex = /^[a-zA-Z0-9_]{4,20}$/;
    if (!usernameRegex.test(username)) {
      setError('아이디는 영문, 숫자, 언더스코어만 사용 가능하며 4~20자여야 합니다.');
      return;
    }

    setCheckingUsername(true);
    setError('');

    try {
      const { data, error } = await supabase.functions.invoke('check-username', {
        body: { username },
      });

      if (error) {
        console.error('Username check error:', error);
        setError('중복확인 중 오류가 발생했습니다.');
        return;
      }

      if (data.available) {
        setUsernameAvailable(true);
        setUsernameChecked(true);
        setError('');
      } else {
        setUsernameAvailable(false);
        setUsernameChecked(true);
        setError(data.message || '이미 사용 중인 아이디입니다.');
      }
    } catch (err) {
      console.error('Username check error:', err);
      setError('중복확인 중 오류가 발생했습니다.');
    } finally {
      setCheckingUsername(false);
    }
  };

  // 아이디 변경 시 중복확인 초기화
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setUsernameChecked(false);
    setUsernameAvailable(false);
  };

  // 필수 항목 완료 여부 체크
  const isFormValid = () => {
    // 기본 정보 필수 (14세 미만은 본인 전화번호 선택)
    const phoneValid = user_type === 'minor'
      ? true  // minor는 본인 전화번호 선택사항
      : phone.replace(/[^\d]/g, '').length >= 10;

    const basicValid =
      name.trim().length >= 2 &&
      birthdate !== '' &&
      gender !== '' &&
      phoneValid;

    // 계정 정보 필수
    const accountValid =
      username.trim() !== '' &&
      usernameChecked &&
      usernameAvailable &&
      emailLocal.trim() !== '' &&
      emailDomain.trim() !== '' &&
      password.trim() !== '' &&
      passwordConfirm.trim() !== '' &&
      password === passwordConfirm;

    // 주소 필수
    const addressValid = zipCode !== '' && address !== '';

    const commonValid = basicValid && accountValid && addressValid;

    // 기관: 기관명 필수
    if (user_type === 'institution') {
      return commonValid && institutionName.trim() !== '';
    }

    // 14세 미만: 보호자 이름 + SMS 인증 + 학교/학년 필수
    if (user_type === 'minor') {
      return commonValid && guardianName.trim().length >= 2 && guardianVerified && school.trim() !== '' && grade !== '';
    }

    return commonValid;
  };

  // 다음 우편번호 서비스 열기
  const openDaumPostcode = () => {
    new (window as any).daum.Postcode({
      oncomplete: function(data: any) {
        // 우편번호와 주소 정보를 해당 필드에 넣는다
        setZipCode(data.zonecode);

        // 도로명 주소와 지번 주소 중 사용자가 선택한 것을 사용
        const fullAddress = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        setAddress(fullAddress);

        // 상세주소 입력 필드로 포커스 이동
        document.getElementById('addressDetail')?.focus();
      }
    }).open();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사 헬퍼 함수
    const showValidationError = (message: string) => {
      setError(message);
      alert(message);
    };

    // 기본 정보 유효성 검사
    if (name.trim().length < 2) {
      showValidationError('이름을 입력해주세요. (최소 2자)');
      return;
    }

    if (!birthdate) {
      showValidationError('생년월일을 입력해주세요.');
      return;
    }

    if (!gender) {
      showValidationError('성별을 선택해주세요.');
      return;
    }

    const phoneNumbers = phone.replace(/[^\d]/g, '');
    // 14세 미만은 본인 전화번호 선택사항
    if (user_type !== 'minor' && phoneNumbers.length < 10) {
      showValidationError('올바른 연락처를 입력해주세요.');
      return;
    }

    // 계정 정보 유효성 검사
    if (!username || !emailLocal || !emailDomain || !password || !passwordConfirm) {
      showValidationError('모든 필수 항목을 입력해주세요.');
      return;
    }

    // 아이디 중복확인 검사
    if (!usernameChecked || !usernameAvailable) {
      showValidationError('아이디 중복확인을 해주세요.');
      return;
    }

    // 이메일 아이디 검증
    if (!/^[a-zA-Z0-9._-]+$/.test(emailLocal)) {
      showValidationError('이메일 아이디는 영문, 숫자, 점, 하이픈, 언더스코어만 사용할 수 있습니다.');
      return;
    }

    // 이메일 도메인 검증
    if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailDomain)) {
      showValidationError('올바른 이메일 도메인을 입력해주세요.');
      return;
    }

    // 전체 이메일 조합
    const email = `${emailLocal}@${emailDomain}`;

    if (password !== passwordConfirm) {
      showValidationError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 6) {
      showValidationError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    if (user_type === 'institution' && !institutionName) {
      showValidationError('기관명을 입력해주세요.');
      return;
    }

    // 주소 필수 검증
    if (!zipCode || !address) {
      showValidationError('주소를 입력해주세요.');
      return;
    }

    // 14세 미만: 보호자 이름 + 인증 + 학교/학년 필수
    if (user_type === 'minor') {
      if (guardianName.trim().length < 2) {
        showValidationError('보호자 이름을 입력해주세요. (최소 2자)');
        return;
      }
      if (!guardianVerified) {
        showValidationError('보호자 SMS 인증을 완료해주세요.');
        return;
      }
      if (!school.trim()) {
        showValidationError('학교를 입력해주세요.');
        return;
      }
      if (!grade) {
        showValidationError('학년을 선택해주세요.');
        return;
      }
    }

    setLoading(true);

    try {
      // Edge Function 호출: signup-complete
      // 모든 회원가입 로직은 서버에서 처리
      const { data, error: fnError } = await supabase.functions.invoke<SignupCompleteResponse>(
        'signup-complete',
        {
          body: {
            name: name.trim(),
            birthdate,
            gender,
            phone: phoneNumbers,
            username,
            email,
            password,
            user_type,
            address: zipCode && address ? `(${zipCode}) ${address}` : '',
            address_detail: addressDetail,
            institution_name: institutionName,
            institution_type: institutionType,
            business_registration_number: businessNumber,
            // 14세 미만 보호자 인증 정보
            guardian_verification_token: guardianVerificationToken || undefined,
            guardian_phone: guardianVerified ? guardianPhone.replace(/[^\d]/g, '') : undefined,
            guardian_name: user_type === 'minor' ? guardianName.trim() : undefined,
            // 학생 정보 (14세 미만: 필수, 일반회원: 선택)
            school: school ? school.trim() : undefined,
            grade: grade || undefined,
          }
        }
      );

      // 디버깅: 전체 응답 출력
      console.log('Edge Function response data:', data);
      console.log('Edge Function response error:', fnError);

      if (fnError) {
        console.error('Edge Function error:', fnError);
        // FunctionsHttpError에서 실제 응답 body 확인
        const errorBody = (fnError as any).context?.body;
        console.log('Error response body:', errorBody);

        // ReadableStream인 경우 읽기 시도
        if (errorBody instanceof ReadableStream) {
          try {
            const reader = errorBody.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            console.log('Error body text:', text);
            const parsed = JSON.parse(text);
            console.log('Parsed error:', parsed);
            if (parsed?.message) {
              setError(parsed.message + (parsed.debug ? `\n상세: ${parsed.debug}` : ''));
              return;
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }
        } else if (errorBody?.message) {
          setError(errorBody.message);
          return;
        }
        throw fnError;
      }

      if (!data?.success) {
        setError(data?.message || '회원가입에 실패했습니다.');
        return;
      }

      // 성공 시 세션 설정 및 완료 페이지로 이동
      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      navigate('/signup/complete', {
        state: {
          session: data.session,
          user: data.user,
        }
      });

    } catch (err: any) {
      console.error('Signup error:', err);

      // 에러 메시지 파싱
      let errorMessage = '회원가입 중 오류가 발생했습니다.';

      if (err.message?.includes('Failed to send a request')) {
        errorMessage = 'Edge Function에 연결할 수 없습니다. 서버를 확인해주세요.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user_type) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50 py-16 mt-20">
        <div className="max-w-5xl mx-auto px-4">
        {/* Contents Head */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h3 className="text-3xl font-bold text-gray-900">
              {user_type === 'minor' ? '14세 미만 회원가입' : '통합회원가입'}
            </h3>
            <div className="flex gap-4 text-sm">
              <Link to="/login" className="text-gray-600 hover:text-[#48c9a6]">로그인</Link>
              <Link to="/signup" className="text-[#48c9a6] font-medium">
                {user_type === 'minor' ? '14세 미만 회원가입' : '통합회원가입'}
              </Link>
            </div>
          </div>
          <div className="h-1 bg-[#48c9a6] w-20 mt-4"></div>
        </div>

        {/* Contents Body */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* 회원정보 입력 타이틀 */}
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 bg-[#48c9a6]/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-[#48c9a6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">회원정보 입력</span>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* 가입 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 기본 정보 섹션 */}
            <div className="pb-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                    placeholder="실명을 입력해주세요"
                    maxLength={20}
                  />
                </div>

                {/* 생년월일 */}
                <div>
                  <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 mb-1">
                    생년월일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="birthdate"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                  />
                </div>

                {/* 성별 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    성별 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setGender('male')}
                      className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
                        gender === 'male'
                          ? 'border-[#48c9a6] bg-[#48c9a6]/10 text-[#48c9a6]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      남성
                    </button>
                    <button
                      type="button"
                      onClick={() => setGender('female')}
                      className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
                        gender === 'female'
                          ? 'border-[#48c9a6] bg-[#48c9a6]/10 text-[#48c9a6]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      여성
                    </button>
                  </div>
                </div>

                {/* 연락처 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    연락처 {user_type !== 'minor' && <span className="text-red-500">*</span>}
                    {user_type === 'minor' && <span className="text-gray-400 text-xs ml-1">(선택)</span>}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                    placeholder="010-0000-0000"
                    maxLength={13}
                  />
                  {user_type === 'minor' && (
                    <p className="mt-1 text-xs text-gray-500">14세 미만은 보호자 연락처로 연락드립니다.</p>
                  )}
                </div>
              </div>
            </div>

            {/* 보호자 SMS 인증 섹션 (14세 미만만) */}
            {user_type === 'minor' && (
              <div className="pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  보호자 인증 <span className="text-red-500">*</span>
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  만 14세 미만 회원은 보호자(법정대리인)의 동의가 필요합니다. 보호자 휴대폰으로 인증번호를 발송합니다.
                </p>

                {/* 보호자 이름 입력 */}
                <div className="mb-4">
                  <label htmlFor="guardianName" className="block text-sm font-medium text-gray-700 mb-1">
                    보호자 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="guardianName"
                    value={guardianName}
                    onChange={(e) => setGuardianName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                    placeholder="보호자 실명"
                    maxLength={20}
                  />
                </div>

                {/* 보호자 전화번호 입력 */}
                <div className="mb-4">
                  <label htmlFor="guardianPhone" className="block text-sm font-medium text-gray-700 mb-1">
                    보호자 연락처 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      id="guardianPhone"
                      value={guardianPhone}
                      onChange={handleGuardianPhoneChange}
                      disabled={guardianVerified}
                      className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent ${
                        guardianVerified
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300'
                      }`}
                      placeholder="010-0000-0000"
                      maxLength={13}
                    />
                    <button
                      type="button"
                      onClick={handleSendCodeClick}
                      disabled={sendingCode || guardianVerified || guardianPhone.replace(/[^\d]/g, '').length < 10}
                      className="px-4 py-3 bg-[#48c9a6] text-white text-sm font-medium rounded-lg hover:bg-[#3ab89a] disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {sendingCode ? '발송중...' : codeSent ? '재발송' : '인증번호 발송'}
                    </button>
                  </div>
                </div>

                {/* 인증코드 입력 (발송 후에만 표시) */}
                {codeSent && !guardianVerified && (
                  <div className="mb-4">
                    <label htmlFor="guardianCode" className="block text-sm font-medium text-gray-700 mb-2">
                      인증번호 입력 <span className="text-[#48c9a6] font-bold">(6자리)</span>
                      {remainingTime > 0 && (
                        <span className="ml-2 text-sm text-amber-600">
                          ({Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')} 남음)
                        </span>
                      )}
                      {remainingTime === 0 && (
                        <span className="ml-2 text-sm text-red-500">만료됨</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        id="guardianCode"
                        value={guardianCode}
                        onChange={(e) => setGuardianCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                        placeholder="인증번호 6자리"
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                      />
                      <button
                        type="button"
                        onClick={verifyGuardianCode}
                        disabled={verifyingCode || guardianCode.length !== 6 || remainingTime === 0}
                        className="px-6 py-3 bg-[#48c9a6] text-white text-sm font-medium rounded-lg hover:bg-[#3ab89a] disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {verifyingCode ? '확인중...' : '확인'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">보호자 휴대폰으로 발송된 인증번호 6자리를 입력해주세요.</p>

                    {/* 테스트 모드 안내 */}
                    {testCode && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800">
                          <strong>[테스트 모드]</strong> 실제 SMS가 발송되지 않습니다.
                        </p>
                        <p className="text-sm text-amber-800 mt-1">
                          테스트 인증코드: <span className="font-mono font-bold">{testCode}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* 인증 완료 표시 */}
                {guardianVerified && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-800 font-medium">보호자 인증이 완료되었습니다.</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 학생 정보 섹션 (14세 미만만) */}
            {user_type === 'minor' && (
              <div className="pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">학생 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 학교 */}
                  <div>
                    <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
                      학교 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="school"
                      value={school}
                      onChange={(e) => setSchool(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                      placeholder="학교명"
                      maxLength={50}
                    />
                  </div>

                  {/* 학년 */}
                  <div>
                    <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
                      학년 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="grade"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent bg-white"
                    >
                      <option value="">선택</option>
                      <option value="초등1">초등학교 1학년</option>
                      <option value="초등2">초등학교 2학년</option>
                      <option value="초등3">초등학교 3학년</option>
                      <option value="초등4">초등학교 4학년</option>
                      <option value="초등5">초등학교 5학년</option>
                      <option value="초등6">초등학교 6학년</option>
                      <option value="중등1">중학교 1학년</option>
                      <option value="중등2">중학교 2학년</option>
                      <option value="중등3">중학교 3학년</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* 계정 정보 섹션 */}
            <div className="pb-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">계정 정보</h3>
              <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                아이디 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={handleUsernameChange}
                  className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6] ${
                    usernameChecked
                      ? usernameAvailable
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="영문, 숫자, 언더스코어 (4~20자)"
                  required
                />
                <button
                  type="button"
                  onClick={checkUsernameAvailability}
                  disabled={checkingUsername || !username.trim()}
                  className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {checkingUsername ? '확인중...' : '중복확인'}
                </button>
              </div>
              {usernameChecked && usernameAvailable && (
                <p className="mt-1 text-xs text-green-600">사용 가능한 아이디입니다.</p>
              )}
              {!usernameChecked && (
                <p className="mt-1 text-xs text-gray-500">로그인 시 사용할 아이디입니다 (4~20자)</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    id="emailLocal"
                    value={emailLocal}
                    onChange={(e) => setEmailLocal(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="이메일 아이디"
                    maxLength={50}
                    required
                  />
                  <span className="text-gray-500">@</span>
                  <input
                    type="text"
                    id="emailDomain"
                    value={emailDomain}
                    onChange={(e) => setEmailDomain(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="도메인"
                    maxLength={50}
                    required
                  />
                  <select
                    onChange={handleDomainSelect}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6] bg-white"
                    title="이메일 도메인 선택"
                  >
                    <option value="">직접입력</option>
                    {emailDomains.map((domain) => (
                      <option key={domain} value={domain}>
                        {domain}
                      </option>
                    ))}
                  </select>
                </div>
                {domainSuggestion && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      혹시 <span className="font-semibold text-gray-900">{domainSuggestion}</span>을 의미하셨나요?
                    </p>
                    <button
                      type="button"
                      onClick={applyDomainSuggestion}
                      className="ml-4 text-sm text-[#48c9a6] hover:text-[#3ab89a] font-medium whitespace-nowrap"
                    >
                      수정
                    </button>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">영수증, 알림 수신용 이메일입니다</p>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="최소 6자 이상"
                required
              />
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700">
                비밀번호 확인 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="passwordConfirm"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6] ${
                  passwordConfirm && password !== passwordConfirm
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder="비밀번호를 다시 입력하세요"
                required
              />
              {passwordConfirm && password !== passwordConfirm && (
                <p className="mt-1 text-xs text-red-500">비밀번호가 일치하지 않습니다.</p>
              )}
              {passwordConfirm && password === passwordConfirm && (
                <p className="mt-1 text-xs text-green-600">비밀번호가 일치합니다.</p>
              )}
            </div>
              </div>
            </div>

            {/* 주소 섹션 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                주소 <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  id="zipCode"
                  value={zipCode}
                  readOnly
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                  placeholder="우편번호"
                />
                <button
                  type="button"
                  onClick={openDaumPostcode}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md border border-gray-300 transition-colors"
                >
                  우편번호 검색
                </button>
              </div>
              <input
                type="text"
                id="address"
                value={address}
                readOnly
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 mb-2"
                placeholder="기본주소 (우편번호 검색 후 자동입력)"
              />
              <input
                type="text"
                id="addressDetail"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="상세주소 (동/호수 등)"
              />
              <p className="mt-1 text-xs text-gray-500">우편번호 검색 버튼을 클릭하여 주소를 입력해주세요</p>
            </div>

            {/* 학교/학년 섹션 (일반회원 - 선택) */}
            {user_type === 'adult' && (
              <div className="pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  추가 정보 <span className="text-gray-400 text-sm font-normal">(선택)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 학교 */}
                  <div>
                    <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
                      학교
                    </label>
                    <input
                      type="text"
                      id="school"
                      value={school}
                      onChange={(e) => setSchool(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                      placeholder="학교명 (선택)"
                      maxLength={50}
                    />
                  </div>

                  {/* 학년 */}
                  <div>
                    <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
                      학년
                    </label>
                    <select
                      id="grade"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent bg-white"
                    >
                      <option value="">선택 안함</option>
                      <option value="초등1">초등학교 1학년</option>
                      <option value="초등2">초등학교 2학년</option>
                      <option value="초등3">초등학교 3학년</option>
                      <option value="초등4">초등학교 4학년</option>
                      <option value="초등5">초등학교 5학년</option>
                      <option value="초등6">초등학교 6학년</option>
                      <option value="중등1">중학교 1학년</option>
                      <option value="중등2">중학교 2학년</option>
                      <option value="중등3">중학교 3학년</option>
                      <option value="고등1">고등학교 1학년</option>
                      <option value="고등2">고등학교 2학년</option>
                      <option value="고등3">고등학교 3학년</option>
                      <option value="대학1">대학교 1학년</option>
                      <option value="대학2">대학교 2학년</option>
                      <option value="대학3">대학교 3학년</option>
                      <option value="대학4">대학교 4학년</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* 기관 전용 필드
                NOTE: 현재 SignupStart에서 adult/minor만 선택 가능하므로 이 섹션은 도달 불가
                향후 기관 가입이 필요하면 SignupStart에 institution 옵션 추가 필요
            */}
            {user_type === 'institution' && (
              <>
                <div>
                  <label htmlFor="institutionName" className="block text-sm font-medium text-gray-700">
                    기관명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="institutionName"
                    value={institutionName}
                    onChange={(e) => setInstitutionName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="기관명을 입력하세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="institutionType" className="block text-sm font-medium text-gray-700">
                    기관 유형
                  </label>
                  <input
                    type="text"
                    id="institutionType"
                    value={institutionType}
                    onChange={(e) => setInstitutionType(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="예: 학교, 복지관 등"
                  />
                </div>

                <div>
                  <label htmlFor="businessNumber" className="block text-sm font-medium text-gray-700">
                    사업자등록번호
                  </label>
                  <input
                    type="text"
                    id="businessNumber"
                    value={businessNumber}
                    onChange={(e) => setBusinessNumber(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="000-00-00000"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-[#48c9a6] hover:bg-[#3ab89a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#48c9a6] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? '가입 처리 중...' : '가입'}
            </button>
            {!isFormValid() && !loading && (
              <p className="mt-2 text-xs text-gray-500 text-center">
                * 표시된 필수 항목을 모두 입력하고 아이디 중복확인을 완료해주세요.
              </p>
            )}
          </form>

          {/* 이전 단계 버튼 */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/signup/terms', { state: { user_type } })}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← 이전 단계로 돌아가기
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* 보호자 동의 안내 모달 */}
      {showGuardianConsentModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-5 overflow-y-auto flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-4">보호자 동의 안내</h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-4">
                <p>
                  만 14세 미만 아동의 회원가입을 위해서는 「개인정보보호법」 제22조에 따라 법정대리인(보호자)의 동의가 반드시 필요합니다.
                </p>

                <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={guardianConsentAgreed}
                    onChange={(e) => setGuardianConsentAgreed(e.target.checked)}
                    className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#48c9a6] focus:ring-[#48c9a6]"
                  />
                  <span className="text-gray-800">
                    본인은 법정대리인으로서, 동두천시글로벌인재교육센터가 관련 법령에 따라 만 14세 미만 아동의 개인정보를 회원가입 및 교육서비스 제공 목적으로 수집·이용하는 것에 동의합니다.
                  </span>
                </label>

                <p>
                  아래 <strong>문자 받기</strong> 버튼을 누르시면 입력한 보호자 휴대전화 번호로 <strong>[회원가입 동의 요청 문자(SMS)]</strong>가 발송되며, 보호자가 해당 문자를 확인하고 인증번호를 입력하여야 회원가입이 완료됩니다.
                </p>

                <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-200">
                  <p>※ 보호자가 동의하지 않을 경우 회원가입이 승인되지 않습니다.</p>
                  <p>※ 보호자에게 문자를 보내기 위해 아동이 입력한 보호자 전화번호는 동의 확인 목적 외 다른 용도로 사용되지 않습니다.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t">
              <button
                type="button"
                onClick={() => setShowGuardianConsentModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={sendGuardianCode}
                disabled={!guardianConsentAgreed || sendingCode}
                className="px-5 py-2 bg-[#48c9a6] text-white text-sm font-medium rounded-md hover:bg-[#3ab89a] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sendingCode ? '발송 중...' : '문자 받기'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SignupForm;
